//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { DateUtils } from 'unison';

import { IAliasedDateRange } from '@components/date-range-picker/dateRangePickerModels';
import DateString from '@models/DateString';
import { IUnisonDateRange } from '@models/UnisonTypes';

// Do we really want to do this to get at the session date range?
declare global {
  interface Window {
    crl8: any;
  }
}

/**
 * Returns date as a string like "August 30, 2019"
 */
export function getReadableDateString(date: Date | DateString): string {
  if (typeof date === 'string') {
    // DateString - use format method to avoid timezone conversion issues
    return DateString.format(date, 'MMMM D, YYYY');
  }
  // Date object
  const month = date.toLocaleString('default', { month: 'long' });
  return `${month} ${date.getDate()}, ${date.getFullYear()}`;
}

/**
 * Returns date as a string without the year like "August 30"
 *
 */
export function getReadableDateStringWithoutYear(date: Date | DateString) {
  if (typeof date === 'string') {
    // DateString - use format method to avoid timezone conversion issues
    return DateString.format(date, 'MMMM D');
  }
  // Date object
  const month = date.toLocaleString('default', { month: 'long' });
  return `${month} ${date.getDate()}`;
}

function generateDateRangeString(
  displayableFrom: string,
  displayableTo: string,
) {
  return displayableFrom === displayableTo
    ? `${displayableTo}`
    : `${displayableFrom} - ${displayableTo}`;
}

export function getReadableDateRangeString(
  dateRange: IAliasedDateRange | IUnisonDateRange,
) {
  const fromDate = getReadableDateString(dateRange.startDate);
  const toDate = getReadableDateString(dateRange.endDate);

  // If this report if for a single day, show that date.
  // Otherwise, display the range.
  return generateDateRangeString(fromDate, toDate);
}

/**
 * This uses the session date range and returns the range as a string
 * like "September 26, 2022 - October 25, 2022"
 *
 */
export function getReadableDateRangeStringFromSession() {
  const sessionDate = window.crl8.session.dateRange;
  const fromDate = getReadableDateString(new Date(sessionDate.from));
  const toDate = getReadableDateString(new Date(sessionDate.to));

  return generateDateRangeString(fromDate, toDate);
}

/**
 * This takes an IUnisonDateRange and returns a date range string that only includes the year once if both dates are in the same year.
 * It returns the range as a string like "September 26 - October 25, 2022"
 *
 */
export function getReadableDateRangeStringWithNoYearRepeat(
  dateRange: IUnisonDateRange,
) {
  const startDate = new Date(dateRange.startDate);
  const endDate = new Date(dateRange.endDate);

  const fromDate =
    startDate.getFullYear() === endDate.getFullYear()
      ? getReadableDateStringWithoutYear(startDate)
      : getReadableDateString(startDate);

  const toDate = getReadableDateString(endDate);

  return generateDateRangeString(fromDate, toDate);
}

export function getSupportedReportDateRanges(): DateUtils.UnisonDateRangeAlias[] {
  return ['last24Hours', 'last7Days', 'last30Days', 'last3Months', 'custom'];
}

export const getPdfDownloadDateRange = (dateRange: IUnisonDateRange) => {
  // If the user opens the PDF download modal, we want the initial date range to be
  //   whatever is currently set on the summary page itself.
  // Check to make sure that the summary date range is in the set of date ranges we're
  //   supporting for PDF downloads. If it's not, default to last7Days.
  const alias = dateRange.alias;
  if (getSupportedReportDateRanges().includes(alias)) {
    return dateRange;
  } else {
    return null;
  }
};
